import { OutputEmitterRef, Signal, Type } from '@angular/core';
import { NodeData } from './node.interface';

import { FormGroup } from '@angular/forms';
import { TableAction } from '@capturum/ui/api';
import { DateTimeRangeValue } from '@core/dynamic-filters/interfaces/date-time-range-filter.interface';
import { FlowPriority } from '@core/enums/flow-priority.enum';
import { Tag } from '@features/tag/interfaces/tag.interface';
import { CurveFactory } from 'd3-shape';
import { ClassicPreset, GetSchemes } from 'rete';
import { AngularArea2D } from 'rete-angular-plugin/21';
import { Position } from '@core/interfaces/position.interface';
import { Side } from 'rete-connection-plugin';
import { MinimapExtra } from 'rete-minimap-plugin';
import { NodeEditorTypeComponent } from '../components/node-editor-type/node-editor-type.component';
import { FlowLogLevel } from '../enums/criticality-level.enum';
import { CoreNodesEnum, TriggerNodesEnum } from '../services/graph.utils';

export class FlowNode<D = NodeData> extends ClassicPreset.Node {
  width = 160;
  height = 130;
  nodeData: D;
  node_data: D;
  override selected = false;
  position?: Position;
  typeVersion = 1;
  name?: string;
}

export class Connection<N extends FlowNode> extends ClassicPreset.Connection<N, N> {
  isMagnetic? = true;
  isLoop? = false;
  curve?: CurveFactory;
  isPseudo?: boolean;
  loopParent?: string;
  waypoints?: Position[];
}
export type Schemes = GetSchemes<FlowNode, Connection<FlowNode>>;
export type DoubleClickNode<F = FlowNode> = {
  type: 'dblclick';
  data: F;
  event: Event;
};
export type AreaExtra =
  | AngularArea2D<Schemes>
  | MinimapExtra
  | {
      type: 'dblclick' | 'nodepicked' | 'nodedragged' | 'connectioncreated';
      data: FlowNode;
      event: Event;
    }
  | DoubleClickNode;

export type ZoomDirection = 'in' | 'out';
export interface SocketData {
  type: 'socket';
  side: Side;
  key: string;
  nodeId: string;
  payload?: any;
  seed: number;
}

export enum SocketType {
  INPUT = 'input',
  OUTPUT = 'output',
}

export interface Flow {
  id: string;
  name: string;
  version?: number;
  is_latest?: boolean;
  version_message?: string;
  readonly?: boolean;
  nodes?: FlowNode[];
  connections?: Connection<FlowNode>[];
  message?: string;
  state?: FlowState;
  slug?: string;
  named_version?: string;
  hasUnsavedChanges?: boolean;
  http_proxy_id?: string | number;
  // Stored on the backend as a ;-separated string. The flow-settings chips control widens this to string[].
  error_email_addresses?: string | null;
  maintenance_from?: string | Date;
  maintenance_until?: string | Date;
  priority?: FlowPriority;
  has_run?: boolean;
  has_warnings?: boolean;
  has_errors?: boolean;
  queued_runs?: number;
  last_executed_at?: string;
  folder_id?: number;
  description?: string;
  tags?: Tag[];
}

export interface FlowVersion {
  id: string;
  version: number;
  named_version?: string;
  is_latest: boolean;
  version_message?: string;
  name: string;
  state?: FlowState;
  created_at: string | Date;
}

export interface FlowSaveAsNewVersionPayload {
  version_message?: string;
  set_as_latest: boolean;
}

export type FlowSetting = Pick<Flow, 'http_proxy_id' | 'description' | 'tags' | 'priority'> & {
  error_email_addresses?: string[];
  isMaintenanceScheduled?: boolean;
  maintenanceRange?: DateTimeRangeValue;
};

export enum FlowState {
  DRAFT = 'draft',
  FINAL = 'final',
  CONCEPT = 'concept',
  MAINTENANCE = 'maintenance',
}

export enum FlowMaintenanceState {
  ON = 'on',
  OFF = 'off',
}

export interface FlowPayload {
  id: string;
  name: string;
  version?: string;
  named_version?: string;
  state: FlowState;
  nodes?: DeepPartial<FlowNode>[];
  connections?: DeepPartial<Connection<FlowNode>>[];
  message?: string;
}

export class InputControl extends ClassicPreset.InputControl<'text' | 'number'> {
  public __type: string;
}

export interface NodeEditorComponentType<NODE = FlowNode, VALUE = Record<string, any>> {
  node: Signal<NODE>;
  updateValue: OutputEmitterRef<VALUE>;
  componentIsRendered: OutputEmitterRef<void>;
  form: FormGroup;
  formValue: VALUE;
  formInitialValue?: Signal<VALUE>;
}

export type NodeEditorComponentTypes = Record<string, NodeEditorComponentComponentCallback>;
export type NodeEditorComponentComponentCallback = () => Promise<Type<NodeEditorTypeComponent>>;

export interface FlowTableAction extends TableAction {
  hiddenExpression?: (item: Flow) => boolean;
}

export interface FlowMaintenancePayload {
  maintenance_ends_at?: string | Date;
}

export interface FlowDuplicatePayload {
  name: string;
  version?: string;
  named_version: string;
  state: FlowState;
  description?: string;
  message?: string;
}

export const FlowWorkerMessageDataType = {
  grid: 'grid',
  pathFinding: 'pathFinding',
  loadFlow: 'loadFlow',
} as const;

export type FlowWorkerMessageDataType = keyof typeof FlowWorkerMessageDataType;
export type FlowGrid = number[][];
export interface FlowWorkerMessageData {
  type: FlowWorkerMessageDataType;
  data: FlowWorkerGridData | FlowWorkerPathFindingData | FlowWorkerLoadFlowData;
  requestId?: string;
}

export interface FlowWorkerPostMessageData {
  type: FlowWorkerMessageDataType;
  data: FlowGrid | Position[] | FlowWorkerLoadFlowResult;
  requestId?: string;
}

export interface FlowWorkerGridData {
  nodes: FlowWorkerNode[];
  canvasWidth: number;
  canvasHeight: number;
  cellSize: number;
  document?: Document;
  translateXY?: {
    x: number;
    y: number;
    z?: number;
  };
}

export type FlowWorkerPathFindingData = {
  requestId?: string;
  points: Position[];
  cellSize?: number;
  grid: FlowGrid;
  nodes: FlowWorkerNode[];
  translateXY?: {
    x: number;
    y: number;
    z?: number;
  };
};

export interface FlowWorkerLoadFlowData {
  flow: Flow;
  coreNodes: Record<CoreNodesEnum, any>;
  triggerNodes: Record<TriggerNodesEnum, any>;
  errorLabel: string;
}

export interface FlowWorkerLoadFlowResult {
  processedNodes: FlowNode[];
  processedConnections: ProcessedConnection[];
  updatedFlow: Flow;
}

export interface ProcessedConnection {
  id: string;
  source: Connection<FlowNode>['source'];
  sourceOutput: string;
  target: Connection<FlowNode>['target'];
  targetInput: string;
  isMagnetic: boolean;
  waypoints?: { x: number; y: number }[];
}

export interface FlowWorkerNode {
  id: number | string;
  position: Position;
  width?: number;
  height?: number;
}

export type PendingPathCallbacks = {
  points: Position[];
  callback: (path: Position[], requestId: string | number) => void;
};

export interface FlowTab {
  label: string;
  icon: string;
  value: number;
  hidden?: () => boolean | Signal<boolean>;
  command?: (item: FlowTab) => void;
  id?: string;
}

export interface FlowLog {
  id?: number;
  created_at: number | Date;
  level: EnumAsUnion<typeof FlowLogLevel>;
  message: string;
  meta?: Record<string, any> | string;
  execution_id?: string;
  new?: boolean;
}

export interface FlowFolder {
  id: number;
  name: string;
  parent_id?: number;
  aggregated_count?: number;
  count?: number;
  children?: FlowFolder[];
}

export interface FlowFolderPayload {
  name: string;
  parent_id?: number;
}

export interface FlowTagPayload {
  name: string;
  color: string;
}
